// Refactored script

/**
 * Configuration object defining the layout for different keys in a grid.
 * Keys are numbers representing different content elements.
 *
 * Each key maps to an object with the following properties:
 *   - slot: An array defining the column placement and colspan.
 *     - If it's an array of length 2 (e.g., [1, 2]), the content spans two columns (colspan=2) occupying columns 1 and 2.
 *     - If it's an array of length 1 (e.g., [1] or [2]), the content spans one column (colspan=1) occupying the specified column number.
 *     - Column numbers are 1-indexed.
 *   - rowspan: The number of rows the content should span vertically.
 */
const layoutConfig = {
  100: {
    slot: [1, 2], // Span columns 1 and 2
    rowspan: 2,
  },
  101: {
    slot: [1],    // Occupy column 1
    rowspan: 2,
  },
  102: {
    slot: [2],    // Occupy column 2
    rowspan: 2,
  },
  200: {
    slot: [1, 2], // Span columns 1 and 2
    rowspan: 1,
  },
  201: {
    slot: [1],    // Occupy column 1
    rowspan: 1,
  },
  202: {
    slot: [2],    // Occupy column 2
    rowspan: 1,
  }
};

// /**
//  * Class representing a 2D grid of GridItems.
//  */
// class Grid {
//   /**
//    * Creates an instance of Grid.
//    * @param {GridItem[][]} gridData - A 2D array of GridItems.
//    */
//   constructor(gridData) {
//     this.gridData = gridData;
//   }

//   /**
//    * Optimizes the grid by moving elements upwards to fill empty spaces.
//    * @returns {Grid} The optimized grid.
//    */
//   optimize() {
//     this.gridData = optimizeGrid(this.gridData);
//     return this;
//   }

//   /**
//    * Removes rows that are completely empty (contain only ' ' in all cells).
//    * @returns {Grid} The grid with empty rows removed.
//    */
//   removeEmptyRows() {
//     this.gridData = removeEmptyRows(this.gridData);
//     return this;
//   }

//   /**
//    * Ensures the grid has at least the specified number of rows and columns.
//    * @param {number} columnCount - The minimum number of columns.
//    * @param {number} rowCount - The minimum number of rows.
//    * @returns {Grid} The grid with the minimum size ensured.
//    */
//   setMinimumSize(columnCount, rowCount) {
//     this.gridData = setMinimumGird(this.gridData, columnCount, rowCount);
//     return this;
//   }

//   /**
//    * Prints the grid to the console using console.table for better visualization.
//    */
//   print() {
//     printGrid(this.gridData);
//   }

//   length = () => this.gridData.length;
// }

/**
 * Generates a grid representation based on the provided list of items and the layout configuration.
 *
 * @param {InputItem[]} itemList - An array of items to display in the grid. Items should have layout codes present in `layoutConfig`.
 * @returns {GridItem[][]} A 2D array representing the grid. Each inner array is a row, and each element is a GridItem.
 * @throws {Error} If a layout code in `itemList` is not found in `layoutConfig`.
 */
function generateGrid(itemList) {
  const grid = [];
  let currentRow = 0;

  // Sort items by layout code in ascending order for consistent processing
  const sortedItemList = [...itemList].sort((a, b) => a.layoutCode - b.layoutCode);

  for (const item of sortedItemList) {
    const config = layoutConfig[item.layoutCode];
    if (!config) {
      throw new Error(`Layout code ${item.layoutCode} not found in layoutConfig`);
    }

    const { slot, rowspan } = config;
    const colspan = slot.length;
    const isColspan = colspan === 2;
    const startColumnIndex = slot[0] - 1; // Adjust to 0-based index

    for (let rowIndexOffset = 0; rowIndexOffset < rowspan; rowIndexOffset++) {
      const actualRowIndex = currentRow + rowIndexOffset;
      if (!grid[actualRowIndex]) {
        grid[actualRowIndex] = Array(2).fill(new GridItem(' ', null)); // Initialize row with 2 columns
      }

      const cellValue = (rowIndexOffset === 0) ? new GridItem(item.layoutCode, item.data,{
        colspan: isColspan ? 2 : 1,
        rowspan,
      }) : new GridItem('↑', item.data); // GridItem for first row, '↑' for rowspan indicators

      if (isColspan) {
        grid[actualRowIndex][0] = cellValue;
        grid[actualRowIndex][1] = (rowIndexOffset === 0) ? new GridItem('←', item.data) : new GridItem('↑', item.data); // '←' for colspan indicator in the first row
      } else {
        grid[actualRowIndex][startColumnIndex] = cellValue;
      }
    }
    currentRow += rowspan;
  }
  return grid;
}

/**
 * Optimizes the grid by moving elements upwards to fill empty spaces.
 *
 * @param {GridItem[][]} grid - The grid to optimize.
 * @returns {GridItem[][]} The optimized grid.
 */
function optimizeGrid(grid) {
  const numRows = grid.length;
  if (numRows <= 1) {
    return grid; // No optimization needed for grids with 0 or 1 rows
  }

  const numCols = grid[0].length;

  for (let colIndex = 0; colIndex < numCols; colIndex++) {
    for (let rowIndex = 1; rowIndex < numRows; rowIndex++) {
      const currentCell = grid[rowIndex][colIndex];
      if (isMovableCell(currentCell)) { // Check if the cell content is movable (key or not indicator)
        const targetRowIndex = findEmptyRowAbove(grid, rowIndex, colIndex);
        if (targetRowIndex !== -1) {
          moveCellUp(grid, rowIndex, colIndex, targetRowIndex);
        }
      }
    }
  }
  return grid;
}

function removeRelatedCell(grid){
  const numRows = grid.length;
  if (numRows === 0) {
    return grid;
  }

  const numCols = grid[0].length;

  for (let colIndex = 0; colIndex < numCols; colIndex++) {
    for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
      const currentCell = grid[rowIndex][colIndex];
      if (currentCell.isRelatedCell()) { // Check if the cell content is movable (key or not indicator)
        grid[rowIndex][colIndex] = new GridItem(' ', null)
      }
    }
  }
  return grid;
}

/**
 * Checks if a cell content is considered movable during grid optimization.
 * Movable cells are GridItems that are not rowspan/colspan indicators or empty spaces.
 *
 * @param {GridItem} cellContent - The content of the cell to check.
 * @returns {boolean} True if the cell content is movable, false otherwise.
 */
function isMovableCell(cellContent) {
  return cellContent instanceof GridItem && !['↑', '←', ' '].includes(cellContent.code);
}

/**
 * Finds the topmost empty row (containing ' ') above the current cell in the same column.
 *
 * @param {GridItem[][]} grid - The grid.
 * @param {number} startRowIndex - The row index to start searching from (exclusive).
 * @param {number} colIndex - The column index to search in.
 * @returns {number} The index of the topmost empty row above, or -1 if no empty row is found.
 */
function findEmptyRowAbove(grid, startRowIndex, colIndex) {
  for (let checkRowIndex = 0; checkRowIndex < startRowIndex; checkRowIndex++) {
    if (grid[checkRowIndex][colIndex].code === ' ') {
      return checkRowIndex;
    }
  }
  return -1;
}

/**
 * Moves a cell and its associated rowspan indicators upwards in the grid.
 *
 * @param {GridItem[][]} grid - The grid.
 * @param {number} fromRowIndex - The original row index of the cell.
 * @param {number} colIndex - The column index of the cell.
 * @param {number} toRowIndex - The target row index to move the cell to.
 */
function moveCellUp(grid, fromRowIndex, colIndex, toRowIndex) {
  const numRows = grid.length;
  const cellContent = grid[fromRowIndex][colIndex];

  grid[toRowIndex][colIndex] = cellContent; // Move element to the topmost available slot
  grid[fromRowIndex][colIndex] = new GridItem(' ', null); // Clear original position

  // Move rowspan indicators '↑' accordingly
  for (let i = fromRowIndex + 1; i < numRows; i++) {
    if (grid[i][colIndex].code === '↑') {
      const targetIndicatorRowIndex = toRowIndex + (i - fromRowIndex);
      if (targetIndicatorRowIndex < numRows) { // Check boundary
        grid[targetIndicatorRowIndex][colIndex] = new GridItem('↑', cellContent.data)
        grid[i][colIndex] = new GridItem(' ', null);
      }
    } else {
      break; // Stop moving rowspan indicators when not found consecutively below
    }
  }
}

/**
 * Removes rows that are completely empty (contain only ' ' in all cells).
 *
 * @param {GridItem[][]} grid - The grid to process.
 * @returns {GridItem[][]} A new grid with empty rows removed.
 */
function removeEmptyRows(grid) {
  return grid.filter(row => row.some(cell => cell.code !== ' '));
}

/**
 * Prints the grid to the console using console.table for better visualization.
 *
 * @param {GridItem[][]} grid - The grid to print.
 */
function printGrid(grid) {
  console.table(grid.map(row => row.map(cell => cell.code)));
}

// if grid does not have enough rows and columns, add empty rows and columns
function setMinimumGird(grid,columnCount,rowCount){
  const numRows = grid.length;
  const numCols = grid[0].length;

  if (numRows < rowCount) {
    for (let i = numRows; i < rowCount; i++) {
      grid.push(Array(numCols).fill(new GridItem(' ', null)));
    }
  }

  if (numCols < columnCount) {
    for (let i = 0; i < rowCount; i++) {
      for (let j = numCols; j < columnCount; j++) {
        grid[i].push(new GridItem(' ', null));
      }
    }
  }

  return grid;
}

/**
 * Runs a series of tests to demonstrate the grid generation and optimization functions.
 *
 * @param {number[]} testKeys - An array of keys to use for testing.
 */
function runTest(testKeys) {
  console.log('--- Test Keys:', testKeys);

  // parse keys into InputItem objects
  const inputItems = testKeys.map((key, index) => new InputItem(key, {
    title: `Title ${index + 1}`,
  }));

  const printMethod = printGrid;

  console.log('Base Grid:');
  const baseGrid = generateGrid(inputItems);
  printMethod(baseGrid);

  console.log('Optimized Grid:');
  const optimizedGrid = optimizeGrid(baseGrid);
  printMethod(optimizedGrid);

  console.log('Grid with Empty Rows Removed:');
  const finalGrid = removeEmptyRows(optimizedGrid);
  printMethod(finalGrid);

  console.log('---');
}


class InputItem{
  constructor(layoutCode,data){
    this.layoutCode = layoutCode;
    this.data = data;

  }
}

class GridItem{
  constructor(code=' ',data=null,tdAttribute={colspan:1,rowspan:1}){
    this.code = code;
    this.data = data?new Schedule(data):null;
    this.tdAttribute = tdAttribute;
  }

  isRelatedCell(){
    return this.code === '↑' || this.code === '←';
  }

  hasLesson(){
    return this.data !== null && !this.isRelatedCell() && !this.isWorkCell();
  }

  isUsedSlot(){
    return this.data !== null
  }

  isAssignable(){
    if(!this.hasLesson()) return false;
    return this.data.teacher_no === 0;
  }

  isTransfer(){
    if(!this.hasLesson()) return false;
    return this.data.isTransfer()
  }

  isWorkCell(){
    if(!this.data) return false;
    return this.data.isWorkCell()
  }

  canTransfer(){
    if(!this.hasLesson()) return false;
    return (!this.data.isTransfer()) && this.isAssignable();
  }

  canRemoveTransfer(){
    if(!this.hasLesson()) return false;
    return this.data.isTransfer()  && this.isAssignable();
  }

  getSectionType(){
    if(!this.isUsedSlot()) return 'empty'
    return this.data.getSectionType()
  }
}

class Schedule{
  constructor(data){
    for (const key in data) {
      if (Object.hasOwnProperty.call(data, key)) {
        this[key] = data[key];
      }
    }
  }

  isRegular(){
    if (this.isWorkCell()) return false;
    if (this.isTransfer()) return false;
    if (this.isLessThen3ClassCount()) return false;
    return  this.class_type === 1
            && this.change_flag === 0
  }

  isAddition(){
    if (this.isWorkCell()) return false;
    if (this.isTransfer()) return false;
    if (this.isLessThen3ClassCount()) return false;
    return (this.class_type !== 1 || this.change_flag === 1)
  }

  isLessThen3ClassCount(){
    if (this.isWorkCell()) return false;
    if (this.isTransfer()) return false;
    return this.class_count <= 3
  }

  isTransfer(){
    if (this.isWorkCell()) return false;
    return this.original_center_no > 0
  }

  isWorkCell(){
    return this.workCellFlag === true
  }

  getSectionType(){
    if(this.isTransfer()) return 'transfer'
    if(this.isRegular()) return 'regular'
    if(this.isAddition()) return 'addition'
    if(this.isLessThen3ClassCount()) return 'lessThen3ClassCount'
    if(this.isWorkCell()) return 'C40101T2050_workCell'
    return 'other'
    // Confirm "T2010TeacherBasicService.C40101_getAppliedSchedule" data when other
  }

  toString(){
    console.log(this)
    console.log({
      isRegular: this.isRegular(),
      isAddition: this.isAddition(),
      isLessThen3ClassCount: this.isLessThen3ClassCount(),
      isTransfer: this.isTransfer(),
    })
    return `checkConsoleLog`
  }
}


// if (require.main === module) {
// runTest([100, 100]);
//   runTest([200, 200]);
//   runTest([101, 102]);
//   runTest([101, 102, 102]);
//   runTest([100, 101, 101, 102, 102]);
//   runTest([101, 201]);
//   runTest([102, 202]);
//   runTest([100, 201, 202]);
// runTest([100, 100, 101, 102, 102, 201, 202, 102]);
// }


function getTimeLimitGroups(timeSlots) {
  const groups = [];
  let group = null;
  for (const date of timeSlots) {
    for (const timeLimit of date.time_limits) {
      const targetGroup = Math.floor(timeLimit.time_limit_no/10)*10;
      if (!group || group.group !== targetGroup) {
        group = {
          date: date.schedule_date,
          group: targetGroup,
          timeLimits: [],
          timeLimitNoS: [],
        }
        groups.push(group);
      }
      group.timeLimits.push(timeLimit);
      group.timeLimitNoS.push(timeLimit.time_limit_no)
    }
  }
  return groups;
}
function generateScheduleGrid(targetSchedules){
  if (!targetSchedules || targetSchedules.length === 0) {
    return [[new GridItem()]]
  }
  // console.log(targetSchedules)
  const inputItems = targetSchedules.map((schedule) => {
    return new InputItem(schedule.course_setting_no, schedule)
  })
  let result = generateGrid(inputItems)
  result = optimizeGrid(result)
  result = removeEmptyRows(result)
  result = setMinimumGird(result,2,2)
  return result
}
function generateScheduleGridWithNoRelatedCell(targetSchedules){
  if (!targetSchedules || targetSchedules.length === 0) {
    return [[new GridItem()]]
  }
  // console.log(targetSchedules)
  const inputItems = targetSchedules.map((schedule) => {
    return new InputItem(schedule.course_setting_no, schedule)
  })
  let result = generateGrid(inputItems)
  result = removeRelatedCell(result)
  result = optimizeGrid(result)
  result = removeEmptyRows(result)
  return result
}
function getTimeLimitGroupsWithSchedules(timeSlots, schedules, teachers) {
  console.time('getTimeLimitGroupsWithSchedules')
  const groups = [];
  let group = null;
  for (const date of timeSlots) {
    for (const timeLimit of date.time_limits) {
      const targetGroup = Math.floor(timeLimit.time_limit_no/10)*10;
      if (!group || group.group !== targetGroup) {
        group = {
          group: targetGroup,
          date: date.schedule_date,
          timeLimits: [],
          timeLimitNoS: [],
          /**
           * @property {GridItem[][]} gridData
           */
          UnassignedSection_regular:{
            gridData: [[new GridItem()]],
            maxRowAcrossTimeLimit: 1,
          },
          UnassignedSection_addition:{
            gridData: [[new GridItem()]],
            maxRowAcrossTimeLimit: 1,
          },
          UnassignedSection_lessThen3ClassCount:{
            gridData: [[new GridItem()]],
            maxRowAcrossTimeLimit: 1,
          },
          UnassignedSection_transfer:{
            gridData: [[new GridItem()]],
            maxRowAcrossTimeLimit: 1,
          },
        }
        groups.push(group);
      }
      group.timeLimits.push(timeLimit);
      group.timeLimitNoS.push(timeLimit.time_limit_no)

      group.schedules = schedules.filter(
        cls => cls.schedule_date === date.schedule_date && group.timeLimitNoS.includes(cls.time_limit_no)
      )

      // #region UnassignedSection
      function unassignedBase(targetSectionData, filteredSchedules){
        const _gridData = generateScheduleGridWithNoRelatedCell(filteredSchedules)
        targetSectionData.gridData = _gridData
        if (_gridData.length > targetSectionData.maxRowAcrossTimeLimit)
          targetSectionData.maxRowAcrossTimeLimit = _gridData.length
        return targetSectionData
      }
      // #region UnassignedSection_regular
      group.UnassignedSection_regular = unassignedBase(group.UnassignedSection_regular, JSON.parse(JSON.stringify(group.schedules))
        .filter(
          cls => {
            return new Schedule(cls).isRegular()
          },
        ))
      // #endregion

      // #region UnassignedSection_addition
      group.UnassignedSection_addition = unassignedBase(group.UnassignedSection_addition, JSON.parse(JSON.stringify(group.schedules))
        .filter(
          cls => {
            return new Schedule(cls).isAddition()
          },
        ))
      // #endregion

      // #region UnassignedSection_lessThen3ClassCount
      group.UnassignedSection_lessThen3ClassCount = unassignedBase(group.UnassignedSection_lessThen3ClassCount, JSON.parse(JSON.stringify(group.schedules))
        .filter(
          cls => {
            return new Schedule(cls).isLessThen3ClassCount()
          },
        ))
      // #endregion

      // #region UnassignedSection_transfer
      group.UnassignedSection_transfer = unassignedBase(group.UnassignedSection_transfer, JSON.parse(JSON.stringify(group.schedules))
        .filter(
          cls => { return new Schedule(cls).isTransfer()},
        ))
      // #endregion
      // console.log('TEtz',group.schedules, _schedule,_schedule2, _schedule1)
    }
  }

  const maxRow= {
    UnassignedSection_regular: 0,
    UnassignedSection_addition: 0,
    UnassignedSection_lessThen3ClassCount: 0,
    UnassignedSection_transfer: 0,
  }
  for (const group of groups){
    if (group.UnassignedSection_regular.gridData.length > maxRow.UnassignedSection_regular)
      maxRow.UnassignedSection_regular = group.UnassignedSection_regular.gridData.length
    if (group.UnassignedSection_addition.gridData.length > maxRow.UnassignedSection_addition)
      maxRow.UnassignedSection_addition = group.UnassignedSection_addition.gridData.length
    if (group.UnassignedSection_lessThen3ClassCount.gridData.length > maxRow.UnassignedSection_lessThen3ClassCount)
      maxRow.UnassignedSection_lessThen3ClassCount = group.UnassignedSection_lessThen3ClassCount.gridData.length
    if (group.UnassignedSection_transfer.gridData.length > maxRow.UnassignedSection_transfer)
      maxRow.UnassignedSection_transfer = group.UnassignedSection_transfer.gridData.length
  }


  for (const group of groups){
    group.UnassignedSection_regular.gridData = setMinimumGird(group.UnassignedSection_regular.gridData,2,maxRow.UnassignedSection_regular)
    group.UnassignedSection_regular.maxRowAcrossTimeLimit = maxRow.UnassignedSection_regular

    group.UnassignedSection_addition.gridData = setMinimumGird(group.UnassignedSection_addition.gridData,2,maxRow.UnassignedSection_addition)
    group.UnassignedSection_addition.maxRowAcrossTimeLimit = maxRow.UnassignedSection_addition

    group.UnassignedSection_lessThen3ClassCount.gridData = setMinimumGird(group.UnassignedSection_lessThen3ClassCount.gridData,2,maxRow.UnassignedSection_lessThen3ClassCount)
    group.UnassignedSection_lessThen3ClassCount.maxRowAcrossTimeLimit = maxRow.UnassignedSection_lessThen3ClassCount

    group.UnassignedSection_transfer.gridData = setMinimumGird(group.UnassignedSection_transfer.gridData,2,maxRow.UnassignedSection_transfer)
    group.UnassignedSection_transfer.maxRowAcrossTimeLimit = maxRow.UnassignedSection_transfer
  }
  console.log(groups)
  console.timeEnd('getTimeLimitGroupsWithSchedules')
  return groups;
}

function insertT2050WorkCellsIntoGrid({
  teacherNo,
  currentDate,
  timeLimitNo
}, C40101T2050Map,gridData) {
  const C40101T2050Data = C40101T2050Map[`${currentDate}_${timeLimitNo}_${teacherNo}`];
  const division = timeLimitNo % 10;
  if(!C40101T2050Data) return gridData;
  const workCellData = {
    teacher_no: teacherNo,
    schedule_date: currentDate,
    time_limit_no: timeLimitNo,
    workCellFlag: true
  };
  // console.debug('Inject C40101T2050 work cell',workCellData,{
  //   teacherNo,
  //   currentDate,
  //   timeLimitNo,
  //   division,
  // },gridData)

  if(division === 1  && gridData[0] && gridData[0][0] && !gridData[0][0].isUsedSlot()) {
    gridData[0][0] = Object.assign(Object.create(Object.getPrototypeOf(gridData[0][0])), gridData[0][0])
    gridData[0][0].data = new Schedule(workCellData);
    gridData[0][0].code = '事務作業';
    gridData[1][0] = Object.assign(Object.create(Object.getPrototypeOf(gridData[1][0])), gridData[1][0])
    gridData[1][0].data = new Schedule(workCellData);
    gridData[1][0].code = '↑';
  }
  if(division === 2 && gridData[0] && gridData[0][1] && !gridData[0][1].isUsedSlot()) {
    gridData[0][1] = Object.assign(Object.create(Object.getPrototypeOf(gridData[0][1])), gridData[0][1])
    gridData[0][1].data = new Schedule(workCellData);
    gridData[0][1].code =  '事務作業';
    gridData[1][1] = Object.assign(Object.create(Object.getPrototypeOf(gridData[1][1])), gridData[1][1])
    gridData[1][1].data = new Schedule(workCellData);
    gridData[1][1].code = '↑';
  }
  return gridData;
}
function insertSupportCellsIntoGrid(gridData, {
  previousGridData,
  nextGridData
}) {
  const rel1 = gridData[0][0].isUsedSlot();
  const rel2 = gridData[0][1].isUsedSlot();

  if (rel1 && rel2) return gridData;
  if (!rel1 && !rel2) return gridData;

  if (rel1) {
    if (nextGridData && nextGridData[0][0].isUsedSlot()) {
      gridData[0][1] = Object.assign(Object.create(Object.getPrototypeOf(gridData[0][1])), gridData[0][1])
      gridData[0][1].isSupport = true;
      gridData[0][1].code = 'サポート';
      gridData[1][1] = Object.assign(Object.create(Object.getPrototypeOf(gridData[1][1])), gridData[1][1])
      gridData[1][1].isSupport = true;
      gridData[1][1].code = '↑';
    }
  } else if (rel2) {
    if (previousGridData && previousGridData[0][1].isUsedSlot()) {
      gridData[0][0] = Object.assign(Object.create(Object.getPrototypeOf(gridData[0][0])), gridData[0][0])
      gridData[0][0].isSupport = true;
      gridData[0][0].code = 'サポート';
      gridData[1][0] = Object.assign(Object.create(Object.getPrototypeOf(gridData[1][0])), gridData[1][0])
      gridData[1][0].isSupport = true;
      gridData[1][0].code = '↑';
    }
  }
  return gridData;

}
export {
  runTest,
  getTimeLimitGroupsWithSchedules,
  getTimeLimitGroups,
  InputItem,
  GridItem,
  Schedule,
  generateGrid,
  optimizeGrid,
  removeEmptyRows,
  setMinimumGird,
  generateScheduleGrid,
  insertT2050WorkCellsIntoGrid,
  insertSupportCellsIntoGrid
};
